import { useMediaQuery } from 'hooks/useMediaQuery';
import { useServerViewport } from 'layouts/viewport/ServerViewportContext';

const maxWidthForBreakpoint = {
    xs: 376,
    sm: 599,
    md: 1024,
    lg: 1239,
    xl: 1444,
    xxl: 1640,
    customBracket: 1490,
};

function useBreakpointDown(maxWidth: number): boolean {
    const matches = useMediaQuery(`(max-width: ${maxWidth}px)`);
    // A phone-only route lays out as a phone from the server on, including in landscape: `sm` and wider read
    // true there, so the server render, hydration and every later render agree.
    const isMobileRoute = useServerViewport() === 'mobile';
    return matches || (isMobileRoute && maxWidth >= maxWidthForBreakpoint.sm);
}

const downDict = Object.fromEntries(
    Object.entries(maxWidthForBreakpoint).map(([k, v]) => [k, `@media (max-width: ${v}px)`]),
) as { [breakpoint in keyof typeof maxWidthForBreakpoint]: string };

const mediaQueryDownDict = Object.fromEntries(
    Object.entries(maxWidthForBreakpoint).map(([k, v]) => [k, `(max-width: ${v}px)`]),
) as { [breakpoint in keyof typeof maxWidthForBreakpoint]: string };

// Flat, `use`-prefixed properties directly on the default export — not nested under an
// `isWidthDown` sub-object — so each call site (`Breakpoint.useIsWidthDownSm()`) is a
// two-level `PascalCaseNamespace.useXxx()` member expression, the only shape React
// Compiler's hook-detection heuristic recognizes for a hook hidden behind property access
// (see `isHook` in babel-plugin-react-compiler). The previous three-level
// `Breakpoint.isWidthDown.sm()` shape was invisible to the compiler, which then silently
// treated these hook calls as ordinary memoizable function calls and could skip/reorder
// them between renders — corrupting hook call order at runtime once a calling component
// (e.g. Navbar) was actually compiled.
export default {
    useIsWidthDownXs: (): boolean => useBreakpointDown(maxWidthForBreakpoint.xs),
    useIsWidthDownSm: (): boolean => useBreakpointDown(maxWidthForBreakpoint.sm),
    useIsWidthDownMd: (): boolean => useBreakpointDown(maxWidthForBreakpoint.md),
    useIsWidthDownLg: (): boolean => useBreakpointDown(maxWidthForBreakpoint.lg),
    useIsWidthDownXl: (): boolean => useBreakpointDown(maxWidthForBreakpoint.xl),
    useIsWidthDownXxl: (): boolean => useBreakpointDown(maxWidthForBreakpoint.xxl),
    useIsWidthDownCustomBracket: (): boolean => useBreakpointDown(maxWidthForBreakpoint.customBracket),
    useIsWidthDownCustom: (maxWidth: number): boolean => useBreakpointDown(maxWidth),
    down: downDict,
    mediaQuery: {
        down: mediaQueryDownDict,
    },
};
